import { useEffect, useState } from 'react'
import { Box, TextField, Typography } from '@mui/material'
import type { TypographyProps } from '@mui/material'

/**
 * Заголовок, который правится по клику прямо на месте.
 *
 * Enter сохраняет, Esc отменяет, потеря фокуса сохраняет — отдельная кнопка
 * «Сохранить» на переименование раздела стоила бы больше, чем само действие.
 */
export function InlineTitleField({
  value,
  onSave,
  disabled = false,
  variant = 'subtitle1',
  placeholder,
}: {
  value: string
  onSave: (title: string) => void | Promise<void>
  disabled?: boolean
  variant?: TypographyProps['variant']
  placeholder?: string
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)

  // Заголовок могли переименовать снаружи (перезагрузка программы после
  // сохранения) — черновик обязан догонять, пока поле не в правке
  useEffect(() => {
    if (!editing) setDraft(value)
  }, [value, editing])

  const commit = () => {
    setEditing(false)
    const next = draft.trim()

    if (next === '' || next === value) {
      setDraft(value)

      return
    }

    void onSave(next)
  }

  if (editing && !disabled) {
    return (
      <TextField
        autoFocus
        size="small"
        value={draft}
        placeholder={placeholder}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            commit()
          }
          if (event.key === 'Escape') {
            setDraft(value)
            setEditing(false)
          }
        }}
        sx={{ minWidth: 240 }}
      />
    )
  }

  return (
    <Box
      component={disabled ? 'div' : 'button'}
      type={disabled ? undefined : 'button'}
      onClick={disabled ? undefined : () => setEditing(true)}
      sx={{
        border: 0,
        p: 0,
        bgcolor: 'transparent',
        font: 'inherit',
        color: 'inherit',
        textAlign: 'left',
        cursor: disabled ? 'default' : 'text',
        borderRadius: 1,
        '&:hover': disabled ? undefined : { bgcolor: 'action.hover' },
      }}
    >
      <Typography variant={variant} fontWeight={600} component="span">
        {value || placeholder}
      </Typography>
    </Box>
  )
}
